import { usePathname } from "expo-router";
import { useEffect, useRef, useState } from "react";
import { Animated, Easing, StyleSheet, View } from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";

import { space } from "@/design/tokens";
import { useAppRouter } from "@/lib/appRouter";
import { useAirPlayController } from "@/playback/airplay/AirPlayController";
import { getCastMiniBottomOffset } from "@/playback/cast/castMiniLayout";
import { useCastController } from "@/playback/cast/CastController";
import type { CastNowPlaying } from "@/playback/cast/CastController.types";
import { CastMiniPill } from "@/playback/cast/CastMiniPill";

const ENTER_MS = 250;
const EXIT_MS = 200;

export function CastMiniControllerImpl() {
  const cast = useCastController();
  const airPlay = useAirPlayController();
  const insets = useSafeAreaInsets();
  const pathname = usePathname();
  const router = useAppRouter();
  const liveItem = cast.nowPlaying ?? airPlay.nowPlaying;
  const [displayedItem, setDisplayedItem] = useState<CastNowPlaying | null>(liveItem);
  const [mounted, setMounted] = useState(liveItem !== null);
  const opacity = useRef(new Animated.Value(0)).current;
  const translateY = useRef(new Animated.Value(12)).current;
  const routeHidden = pathname.startsWith("/watch/");

  useEffect(() => {
    if (routeHidden) {
      opacity.stopAnimation();
      translateY.stopAnimation();
      opacity.setValue(0);
      translateY.setValue(12);
      setMounted(false);
      return;
    }
    if (liveItem) {
      setDisplayedItem(liveItem);
      if (!mounted) {
        opacity.setValue(0);
        translateY.setValue(12);
        setMounted(true);
      }
      const animation = Animated.parallel([
        Animated.timing(opacity, { duration: ENTER_MS, easing: Easing.out(Easing.cubic), toValue: 1, useNativeDriver: true }),
        Animated.timing(translateY, { duration: ENTER_MS, easing: Easing.out(Easing.cubic), toValue: 0, useNativeDriver: true })
      ]);
      animation.start();
      return () => animation.stop();
    }
    if (!mounted) return;
    const animation = Animated.parallel([
      Animated.timing(opacity, { duration: EXIT_MS, easing: Easing.in(Easing.cubic), toValue: 0, useNativeDriver: true }),
      Animated.timing(translateY, { duration: EXIT_MS, easing: Easing.in(Easing.cubic), toValue: 12, useNativeDriver: true })
    ]);
    animation.start(({ finished }) => {
      if (finished) {
        setMounted(false);
        setDisplayedItem(null);
      }
    });
    return () => animation.stop();
  }, [liveItem, mounted, opacity, routeHidden, translateY]);

  const renderedItem = liveItem ?? displayedItem;
  if (routeHidden || !mounted || !renderedItem) return null;
  return (
    <View
      style={[
        styles.host,
        { bottom: getCastMiniBottomOffset(insets.bottom), pointerEvents: "box-none" }
      ]}
    >
      <Animated.View style={{ opacity, transform: [{ translateY }], width: "100%" }}>
        <CastMiniPill
          artworkUrl={renderedItem.artworkUrl}
          currentTime={cast.nowPlaying ? cast.currentTime : airPlay.currentTime}
          deviceName={cast.nowPlaying ? cast.deviceName ?? "TV" : "AirPlay"}
          duration={cast.nowPlaying ? cast.duration : airPlay.duration}
          hasNext={renderedItem.hasNext}
          hasPrevious={renderedItem.hasPrevious}
          onBody={() => router.push(renderedItem.returnPath as never)}
          onNext={cast.nowPlaying ? cast.next : () => void airPlay.next()}
          onPrevious={cast.nowPlaying ? cast.previous : airPlay.previous}
          onTogglePlay={cast.nowPlaying ? cast.togglePlaying : airPlay.togglePlaying}
          itemKey={renderedItem.id}
          playing={cast.nowPlaying ? cast.playing : airPlay.playing}
          route={cast.nowPlaying ? "cast" : "airplay"}
          title={renderedItem.title}
        />
      </Animated.View>
    </View>
  );
}

const styles = StyleSheet.create({
  host: {
    alignItems: "center",
    left: 0,
    paddingHorizontal: space.md,
    position: "absolute",
    right: 0,
    zIndex: 80
  }
});
